import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import {
  ContentCopyOutlined,
  RefreshOutlined,
  CheckOutlined,
} from "@mui/icons-material";
import { SuggestedReplies } from "../types";

interface ReplyGeneratorProps {
  onGenerate: (context: string) => Promise<SuggestedReplies>;
  initialReplies?: SuggestedReplies | null;
  loading?: boolean;
}

const REPLY_STYLES = [
  {
    key: "value-first" as const,
    label: "Value-First",
    description: "Lead with helpful information, subtle product mention",
  },
  {
    key: "educational" as const,
    label: "Educational",
    description: "Share knowledge and expertise, position as thought leader",
  },
  {
    key: "conversational" as const,
    label: "Conversational",
    description: "Friendly and casual engagement",
  },
  {
    key: "problem-solver" as const,
    label: "Problem Solver",
    description: "Direct solution to the user's problem",
  },
];

const ReplyGenerator: React.FC<ReplyGeneratorProps> = ({
  onGenerate,
  initialReplies,
  loading: externalLoading,
}) => {
  const [context, setContext] = useState("");
  const [replies, setReplies] = useState<SuggestedReplies | null>(
    initialReplies || null
  );
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);

  const handleGenerate = async () => {
    if (!context.trim()) {
      setError("Please provide context about your product/service");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await onGenerate(context);
      setReplies(result);
    } catch (err: any) {
      setError(
        err.response?.data?.error || err.message || "Failed to generate replies"
      );
    } finally {
      setLoading(false);
    }
  };

  const getCurrentReply = (): string => {
    if (!replies) return "";
    const style = REPLY_STYLES[selectedTab];
    return replies[style.key] || "";
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getCurrentReply());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const isLoading = loading || externalLoading;
  const hasReplies = replies && Object.keys(replies).length > 0;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Generate AI Replies
      </Typography>

      <TextField
        fullWidth
        multiline
        rows={3}
        size="small"
        label="Context"
        placeholder="Describe your product/service and key features to mention in the reply..."
        value={context}
        onChange={(e) => setContext(e.target.value)}
        disabled={isLoading}
        sx={{ mb: 2 }}
        helperText="This context helps the AI craft relevant and helpful replies for all 4 styles"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        onClick={handleGenerate}
        disabled={isLoading || !context.trim()}
        startIcon={
          isLoading ? (
            <CircularProgress size={16} color="inherit" />
          ) : hasReplies ? (
            <RefreshOutlined />
          ) : undefined
        }
        sx={{ mb: 2 }}
      >
        {isLoading
          ? "Generating all styles..."
          : hasReplies
          ? "Regenerate All"
          : "Generate Replies"}
      </Button>

      {hasReplies && (
        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            overflow: "hidden",
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={(_, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              backgroundColor: "action.hover",
            }}
          >
            {REPLY_STYLES.map((style) => (
              <Tab
                key={style.key}
                label={style.label}
                sx={{ textTransform: "none" }}
              />
            ))}
          </Tabs>

          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  {REPLY_STYLES[selectedTab].label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {REPLY_STYLES[selectedTab].description}
                </Typography>
              </Box>
              <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                <IconButton size="small" onClick={handleCopy}>
                  {copied ? (
                    <CheckOutlined fontSize="small" color="primary" />
                  ) : (
                    <ContentCopyOutlined fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                lineHeight: 1.6,
                mt: 1,
              }}
            >
              {getCurrentReply()}
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ReplyGenerator;
